import React, { useMemo } from 'react';
import {
  DefaultNamespace,
  Trans as DefaultTrans,
  TransProps as DefaultTransProps,
} from 'react-i18next';
import type { Namespace, TFuncKey } from 'i18next';

import type { DictionaryData } from './types';

import getDefaultValue from './get-default-value';
import { useTranslation } from './useTranslation';

// ---

type TransProps<C extends TransComponents> = {
  data: DictionaryData;
  components?: IsCorrectComponents<C>;
};

function Trans<
  K extends TFuncKey<N, TKPrefix> extends infer A ? A : never,
  Components extends TransComponents,
  N extends Namespace = DefaultNamespace,
  TKPrefix = undefined,
  E = React.HTMLProps<HTMLDivElement>
>({
  data,
  ...rest
}: Omit<DefaultTransProps<K, N, TKPrefix, E>, 'components' | 'data'> & TransProps<Components>) {
  const { lang } = useTranslation();

  return useMemo(() =>
    (
      <DefaultTrans {...rest}
                    i18nKey={data.weblateKey}
                    defaults={getDefaultValue(data)} />
    )
  , [lang, rest]);
}

export default Trans;

// ---

/*
* Правило с сайта react-i18n:
* Existing self-closing HTML tag names are reserved keys and won't work. Examples: link: <Link />, img: <img src="" />, media: <img src="" />
* */
type ForbiddenKeys = 'link' | 'img' | 'media';
type TransComponents = { readonly [tagName: string]: React.ReactElement };

type IsCorrectComponents<C extends TransComponents, T = keyof C> =
  keyof C extends ForbiddenKeys
    ? `Ключ ${T & string} запрещено использовать в этом объекте`
    : C;
